import { styled } from '@mui/material/styles';
import { ReactElement, useEffect, useMemo, useState, type JSX } from 'react';

import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useColumnSelector, useSelectorColumns } from '../../../providers/ColumnSelectorProvider';
import { ColumnMeta, SelectItem } from '../../../../../types';
import { SelectedColumnItem } from './SelectedColumnItem';

const Container = styled('div')`
  padding: 28px 10px;
  width: 100%;
  height: 500px;
  overflow-y: scroll;
`;

const ListItem = styled('div')``;

type DraggableColumnItemProps = {
  column: SelectItem;
};

const SortableItem = ({ column }: DraggableColumnItemProps): JSX.Element => {
  const { setNodeRef, transform, transition } = useSortable({ id: column.id as string });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <ListItem ref={setNodeRef} style={style}>
      <SelectedColumnItem column={column} />
    </ListItem>
  );
};

export const SelectedColumns = (): ReactElement => {
  const [selectedColumns, setSelectedColumns] = useState<SelectItem[]>([]);
  const { activeOrder, setActiveOrder } = useColumnSelector();
  const data = useSelectorColumns();
  const columnMap = useMemo(() => new Map<string, ColumnMeta>(data?.map((cm) => [cm.name, cm])), [data]);

  const handleReorder = (e: DragEndEvent) => {
    const { active, over } = e;
    if (active.id !== over?.id) {
      const oldIndex = selectedColumns.findIndex((sc) => sc.id === active.id);
      const newIndex = selectedColumns.findIndex((sc) => sc.id === over?.id);
      const reorderedSelectedColumns = arrayMove(selectedColumns, oldIndex, newIndex);
      const newOrder = reorderedSelectedColumns.filter((c) => !!c).map((c) => c.id as string);
      setSelectedColumns(reorderedSelectedColumns);
      setActiveOrder(newOrder);
    }
  };

  useEffect(() => {
    const result: SelectItem[] = [];
    activeOrder.forEach((c) => {
      const item = columnMap.get(c);
      if (item) {
        result.push({ id: item.name, value: item.displayName });
      }
    });
    setSelectedColumns(result);
  }, [activeOrder, columnMap]);

  return (
    <DndContext onDragEnd={handleReorder}>
      <Container>
        <SortableContext items={selectedColumns as { value: string; id: string }[]}>
          {selectedColumns
            .filter((item) => !!item)
            .map((item) => (
              <SortableItem key={item.id} column={item} />
            ))}
        </SortableContext>
      </Container>
    </DndContext>
  );
};
